import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppRoutes } from 'constants/AppRoutes';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import {
  useGetMaterialityStakeholdersListStatusQuery,
  useUpdateSectionMAMutation,
} from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import { useCreateRecipientUsersMutation, useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import useMaterialityAssessmentInsight from '../../hooks/useMaterialityAssessmentInsight';
import useMaterialityAssessmentQuestion from '../../hooks/useMaterialityAssessmentQuestion';
import MaterialAssessmentSurveyBaseView from './MaterialAssessmentSurveyBaseView';

import type IRecipientCreateRequest from 'store/slices/materialAssessment/interfaces/IRecipientCreateRequest';
import type { IMaterialAssessmentSurveyBaseConfig } from './interfaces/IMaterialityAssessmentSurveyView';

function MaterialAssessmentSurveyBase({ config }: { config: IMaterialAssessmentSurveyBaseConfig }) {
  const navigate = useNavigate();
  const { recordId } = useParams();
  const [openSendSurveyModal, setOpenSendSurveyModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openAddEmailModal, setOpenAddEmailModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const lastStatusUpdateKeyRef = useRef<string | null>(null);

  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId || '' });
  const [updateSection] = useUpdateSectionMAMutation();

  const hideActions = singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const handleGetType = useCallback(() => {
    switch (config.type) {
      case 'SURVEY':
        return SECTION_TYPE.INSIGHT_SURVEY_VCM;

      case 'INTERVIEW':
        return SECTION_TYPE.INSIGHT_INTERVIEWS_VCM;

      default:
        return SECTION_TYPE.INSIGHT_ROUNDTABLES_VCM;
    }
  }, [config.type]);

  const handleSelectItem = (itemId: number) => {
    setSelectedItems((prev) => {
      const currentArray = prev || [];
      const exists = currentArray.includes(itemId);
      return exists
        ? currentArray.filter((id) => id !== itemId)
        : [...currentArray, itemId];
    });
  };

  const { data: userData } = useUserMeQuery();
  const companyId = userData?.data?.companyId;
  const { access } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const [createRecipientUsers] = useCreateRecipientUsersMutation();

  const handleCreateRecipientUsers = async (data: IRecipientCreateRequest) => {
    await createRecipientUsers(data).unwrap();
    toast.success(i18n.t('notifications.createdRecipientUsers'));
  };

  const handlePrevSurvey = () => {
    switch (config.type) {
      case 'SURVEY':
        return navigate(AppRoutes.materialityAssessmentSurveyCreation.replace(':recordId', recordId || ''));

      case 'INTERVIEW':
        return navigate(AppRoutes.materialityAssessmentSurvey.replace(':recordId', recordId || ''));

      case 'ROUNDTABLE':
        return navigate(AppRoutes.materialityAssessmentInterview.replace(':recordId', recordId || ''));

      default:
        return navigate(AppRoutes.materialityAssessmentRoundtables.replace(':recordId', recordId || ''));
    }
  };

  const handleCancelSurvey = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const handleOpenSendSurveyModal = () => {
    setOpenSendSurveyModal(true);
  };

  const handleOpenShareModal = () => {
    setOpenShareModal(true);
  };

  const handleCloseSendSurveyModal = () => {
    setOpenSendSurveyModal(false);
    setOpenShareModal(false);
    setSelectedItems([]);
  };

  const handleCloseShareModal = () => {
    setOpenShareModal(false);
    setSelectedItems([]);
    setOpenSendSurveyModal(false);
  };

  const handleAddEmail = () => {
    setOpenAddEmailModal(true);
  };

  const handleCloseAddEmailModal = () => {
    setOpenAddEmailModal(false);
  };

  const question = useMaterialityAssessmentQuestion(config.insightType);
  const insight = useMaterialityAssessmentInsight(config.insightType);
  const { activeVcTab } = insight;
  const sectionType = handleGetType();

  const {
    data: sectionStatusData,
    isFetching: isSectionStatusFetching,
  } = useGetMaterialityStakeholdersListStatusQuery({
    maId: recordId || '',
    type: sectionType,
    vcmId: activeVcTab || 0,
  }, {
    skip: !recordId || !activeVcTab,
  });

  const questionProps = {
    items: question.itemsList,
    control: question.control,
    onSortChange: question.onSortChange,
    isLoading: question.isLoading,
    loadMoreQuestions: question.loadMoreQuestions,
    hasMore: question.hasMore,
    onEdit: undefined,
    onDelete: undefined,
    onViewMode: undefined,
    handleShareSurvey: question.handleShareSurvey,
  };

  const insightProps = {
    items: insight.itemsList,
    control: insight.control,
    onSortChange: insight.onSortChange,
    isLoading: insight.isLoading,
    loadMoreQuestions: insight.loadMoreInsights,
    hasMore: insight.hasMore,
    onDelete: insight.onDelete,
    onViewMode: undefined,
    activeVcTab,
    handleClickVcTab: insight.handleClickVcTab,
  };

  useEffect(() => {
    if (
      !recordId
      || !activeVcTab
      || isViewOnly
      || !insight.isCurrentInsightDataReady
      || isSectionStatusFetching
      || !sectionStatusData?.data?.status
    ) {
      return;
    }

    const nextStatus = insight.currentInsightsCount > 0
      ? STATUS_SECTIONS.COMPLETED
      : STATUS_SECTIONS.IN_PROGRESS;

    if (sectionStatusData.data.status === nextStatus) {
      lastStatusUpdateKeyRef.current = null;
      return;
    }

    const statusUpdateKey = [sectionType, activeVcTab, nextStatus].join('-');

    if (lastStatusUpdateKeyRef.current === statusUpdateKey) {
      return;
    }

    lastStatusUpdateKeyRef.current = statusUpdateKey;

    updateSection({
      maId: recordId,
      type: sectionType,
      valueChainMapId: activeVcTab,
      data: {
        isActive: true,
        status: nextStatus,
      },
    }).unwrap().catch(() => {
      if (lastStatusUpdateKeyRef.current === statusUpdateKey) {
        lastStatusUpdateKeyRef.current = null;
      }
    });
  }, [
    recordId,
    activeVcTab,
    isViewOnly,
    insight.isCurrentInsightDataReady,
    insight.currentInsightsCount,
    isSectionStatusFetching,
    sectionStatusData?.data?.status,
    sectionType,
    updateSection,
  ]);

  const handleNextSurvey = () => {
    switch (config.type) {
      case 'SURVEY':
        return navigate(AppRoutes.materialityAssessmentInterview.replace(':recordId', recordId || ''));

      case 'INTERVIEW':
        return navigate(AppRoutes.materialityAssessmentRoundtables.replace(':recordId', recordId || ''));

      default:
        return navigate(AppRoutes.materialityAssessmentInsightsDashboard.replace(':recordId', recordId || ''));
    }
  };

  return (
    <MaterialAssessmentSurveyBaseView
      isEmptyList={insight.isEmptyList}
      handleNextSurvey={handleNextSurvey}
      handleCancelSurvey={handleCancelSurvey}
      handlePrevSurvey={handlePrevSurvey}
      questionProps={questionProps}
      insightProps={insightProps}
      config={config}
      mainVcTabs={singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ value: itemVC.name, id: itemVC.id, name: itemVC.name }),
      ) || []}
      handleOpenSendSurveyModal={handleOpenSendSurveyModal}
      handleCloseSendSurveyModal={handleCloseSendSurveyModal}
      openSendSurveyModal={openSendSurveyModal}
      handleOpenShareModal={handleOpenShareModal}
      openShareModal={openShareModal}
      handleCloseShareModal={handleCloseShareModal}
      selectedItems={selectedItems}
      handleSelectItem={handleSelectItem}
      handleAddEmail={handleAddEmail}
      openAddEmailModal={openAddEmailModal}
      handleCloseAddEmailModal={handleCloseAddEmailModal}
      handleCreateRecipientUsers={handleCreateRecipientUsers}
      companyId={companyId || 0}
      isViewOnly={isViewOnly || hideActions}
    />
  );
}

export default MaterialAssessmentSurveyBase;
